/**
 * 4-step progress indicator for the Add Property wizard (COL-529).
 *
 * Renders horizontal pill stepper with circles + labels. Only steps *before*
 * the current one are clickable — jumping back is allowed, jumping forward is
 * not (each step gates "Weiter" on its own validation). `furthestReached`
 * still drives the completed-checkmark styling for steps you reached and left.
 */
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLanguage } from "@/contexts/LanguageContext";
import { WIZARD_STEPS } from "./wizardSteps";

type Props = {
  current: number;
  furthestReached: number;
  onJump: (step: number) => void;
};

export function WizardStepper({ current, furthestReached, onJump }: Props) {
  const { t } = useLanguage();
  return (
    <ol className="flex items-center gap-1 sm:gap-2 mb-6 overflow-x-auto pb-2">
      {WIZARD_STEPS.map((s, i) => {
        const isCurrent = s.n === current;
        const isComplete = s.n < furthestReached || s.n < current;
        // Back-only: a step is clickable only when it comes before the current
        // one. Forward steps stay locked so each is reached through its own
        // "Weiter" gate.
        const isReachable = s.n < current;
        const last = i === WIZARD_STEPS.length - 1;

        return (
          <li key={s.n} className="flex items-center gap-1 sm:gap-2 shrink-0">
            <button
              type="button"
              onClick={() => isReachable && onJump(s.n)}
              disabled={!isReachable}
              className={cn(
                "flex items-center gap-2 rounded-full transition-colors",
                "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                isReachable && !isCurrent
                  ? "cursor-pointer hover:bg-muted px-2 py-1"
                  : "px-2 py-1",
                !isReachable && "cursor-not-allowed opacity-50"
              )}
              aria-current={isCurrent ? "step" : undefined}
            >
              <span
                className={cn(
                  "flex h-7 w-7 items-center justify-center rounded-full text-xs font-semibold border",
                  isCurrent &&
                    "bg-primary text-primary-foreground border-primary",
                  !isCurrent &&
                    isComplete &&
                    "bg-primary/15 text-primary border-primary/30",
                  !isCurrent &&
                    !isComplete &&
                    "bg-background text-muted-foreground border-border"
                )}
              >
                {isComplete ? <Check className="h-3.5 w-3.5" /> : s.n}
              </span>
              <span
                className={cn(
                  "text-xs sm:text-sm font-medium hidden sm:inline",
                  isCurrent ? "text-foreground" : "text-muted-foreground"
                )}
              >
                {t(s.titleKey, s.title)}
              </span>
            </button>
            {!last && (
              <span className="h-px w-4 sm:w-8 bg-border" aria-hidden="true" />
            )}
          </li>
        );
      })}
    </ol>
  );
}
